export const canManageBackups = () => {
  if (localStorage.getItem("is_admin") === "1") return true;
  try {
    return JSON.parse(localStorage.getItem("account_permissions") || "[]").includes("data_backup");
  } catch {
    return false;
  }
};

export const isJobActive = (job) => !!job && ["queued", "running"].includes(job.status);

const RESTORE_PHASES = {
  starting: "Starting",
  validate: "Checking backup file",
  undo_backup: "Saving current data (undo point)",
  load: "Loading backup",
  verify: "Verifying row counts",
  swap: "Applying restored data",
  swapped: "Finishing",
  done: "Done",
};

export const phaseLabel = (job) => (job?.type === "restore" ? RESTORE_PHASES[job.phase] || job.phase : null);

// Backup: rows done / estimated rows (capped at 99% until the file is verified).
// Restore: undo backup is the first 45%, loading the next 50%, verify + switch the rest.
export const jobPercent = (job) => {
  if (!job) return 0;
  if (job.status === "ready") return 100;
  if (job.status !== "running") return 0;
  const frac = job.progress_total > 0 ? Math.min(1, job.progress_done / job.progress_total) : 0;
  if (job.type === "backup") return Math.min(99, Math.floor(frac * 100));
  switch (job.phase) {
    case "undo_backup":
      return Math.floor(frac * 45);
    case "load":
      return 45 + Math.floor(frac * 50);
    case "verify":
      return 96;
    case "swap":
    case "swapped":
      return 99;
    default:
      return 0;
  }
};

export const jobTitle = (job) =>
  job?.type === "restore" ? `Restoring database to backup #${job.restore_of_id}` : `Backing up database (#${job?.id})`;

export const formatNumber = (n) => Number(n || 0).toLocaleString();

export const formatBytes = (bytes) => {
  if (!bytes) return "-";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${units[i]}`;
};

export const formatDuration = (seconds) => {
  if (seconds == null || !isFinite(seconds)) return "";
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

// Relative time from a server-computed age, so browser/server time zones never skew it.
export const formatAgo = (seconds) => {
  if (seconds == null || seconds < 0) return "";
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`;
  const days = Math.floor(seconds / 86400);
  return days === 1 ? "yesterday" : `${days} days ago`;
};

export const progressDetail = (job) => {
  if (!job || job.status !== "running") return "";
  const parts = [];
  if (job.progress_table_count) parts.push(`Table ${job.progress_table_index}/${job.progress_table_count}`);
  if (job.progress_table) parts.push(job.progress_table);
  if (job.progress_total) parts.push(`${formatNumber(job.progress_done)} / ${formatNumber(job.progress_total)} rows`);
  return parts.join(" · ");
};
